import { Icon, IconName } from '@ardoq/icons';
import {
  PanelContainer,
  PanelFooter,
  PanelHeader,
  SectionsContainer,
} from 'appLayout/ardoqStudio/saveAsViewpoint/atoms';
import { PrimaryButton, GhostButton } from '@ardoq/button';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import { hideViewSidePanel } from 'views/mainApp/viewSidePanel/actions';
import { TextAreaWithCounter } from '@ardoq/snowflakes';
import { bookmarkCommands as commands } from './commands';
import {
  saveAsBookmarkViewModel$,
  SaveAsBookmarkViewModelShape,
} from './saveAsBookmarkViewModel$';
import { FolderSelect as FolderSelectBase } from '../components/FolderSelectDialog/FolderSelect';
import { Header4, InlineText, Paragraph } from '@ardoq/typography';
import { Link } from '@ardoq/link';
import { Box, FlexBox, Stack } from '@ardoq/layout';
import { ErrorNotification } from '@ardoq/status-ui';
import { ArdoqError, getArdoqErrorMessage } from '@ardoq/common-helpers';
import { openChatWithUs } from '../components/AppMainSidebar/utils';

type SaveAsBookmarkProps = SaveAsBookmarkViewModelShape;

const SaveAsBookmarkBase = ({
  folderSelectDataSource,
  bookmarkName,
  error,
  selectedFolder,
  isSaveBookmarkEnabled,
  i18nLocale,
  locale,
}: SaveAsBookmarkProps) => {
  return (
    <PanelContainer>
      <PanelHeader>
        <Icon iconName={IconName.BOOKMARK} />
        <span>Save as Bookmark</span>
      </PanelHeader>
      <SectionsContainer>
        <FlexBox
          padding="medium"
          paddingBottom="large"
          gap="large"
          flexDirection="column"
          height="full"
        >
          <TextAreaWithCounter
            autoFocus
            id="save-as-bookmark-name-input"
            label="Bookmark name"
            helperText="Use a short name that describes what this bookmark shows"
            value={bookmarkName}
            onValueChange={commands.setBookmarkName}
            characterLimit={60}
            characterLimitWarning="Long names may not be fully displayed"
          />
          <Stack gap="small" flex={1}>
            <Header4>
              Save to folder
              <InlineText color="textSubtle" variant="text1">
                {' '}
                (optional)
              </InlineText>
            </Header4>
            <FolderSelectBase
              dataSource={folderSelectDataSource}
              selectedFolder={selectedFolder}
              setSelectedFolder={commands.setBookmarkFolder}
              i18nLocale={i18nLocale}
              locale={locale}
            />
          </Stack>
        </FlexBox>
      </SectionsContainer>
      <PanelFooter padding="medium" justify="end">
        <Stack align="flex-end">
          {error && <ErrorMessage error={error} />}
          <FlexBox gap="medium" align="flex-end">
            <GhostButton
              onClick={() => {
                dispatchAction(hideViewSidePanel());
              }}
            >
              Cancel
            </GhostButton>
            <PrimaryButton
              isDisabled={!isSaveBookmarkEnabled}
              onClick={() =>
                commands.saveBookmark(
                  bookmarkName,
                  selectedFolder && selectedFolder._id
                )
              }
            >
              Save bookmark
            </PrimaryButton>
          </FlexBox>
        </Stack>
      </PanelFooter>
    </PanelContainer>
  );
};

const ErrorMessage = ({ error }: { error: ArdoqError }) => {
  const message = getArdoqErrorMessage(error, 'Failed to save bookmark.');
  return (
    <Box marginBottom="medium">
      <ErrorNotification>
        <Stack gap="small">
          <Paragraph>
            {message} If the problem continues, please contact{' '}
            <Link typography="text2" onClick={() => openChatWithUs()}>
              customer support.
            </Link>
          </Paragraph>

          {error.traceId && <Paragraph>Trace ID: {error.traceId}</Paragraph>}
        </Stack>
      </ErrorNotification>
    </Box>
  );
};

export const SaveAsBookmark = connect(
  SaveAsBookmarkBase,
  saveAsBookmarkViewModel$
);
